import React, { useEffect, useState } from 'react';
import { Send, Loader2, CheckCircle2, AlertCircle, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin test-email tool. Sends the article-broadcast email template to a
 * single address the admin picks so they can eyeball it in their inbox
 * before flipping a real article to Published. Defaults to the most recent
 * published article; a dropdown lets them pick a specific one.
 */
const TestEmailPanel = ({ token }) => {
  const [email, setEmail] = useState('');
  const [articleId, setArticleId] = useState(''); // '' = latest published
  const [articles, setArticles] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Load the article list once so the dropdown can show titles.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/articles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const pub = (data.items || []).filter((a) => a.status === 'published');
          setArticles(pub);
        }
      } catch { /* non-fatal */ }
    })();
  }, [token]);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    setResult(null);
    try {
      const body = { email: email.trim() };
      if (articleId) body.article_id = articleId;
      const res = await fetch(`${API}/api/admin/articles/email-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        let msg = data.detail || `HTTP ${res.status}`;
        // Resend sandbox rejects sends to anyone but the account owner. If
        // the user hit that wall, spell out the fix instead of surfacing
        // the raw provider text.
        if (typeof msg === 'string' && /only send testing emails/i.test(msg)) {
          msg = "Your Resend account is still in sandbox mode - it only allows sending to the account owner's email. Update the RESEND_FROM production secret to an address at your verified domain (e.g. press@outlawmedia.com.au) and redeploy, then this test will send to anyone.";
        } else if (typeof msg === 'string' && /verify a domain|domain is not verified/i.test(msg)) {
          msg = "Resend says your sending domain isn't verified yet. Check https://resend.com/domains - every DNS record must show a green tick before it'll send to arbitrary addresses.";
        }
        throw new Error(msg);
      }
      setResult(data);
    } catch (err) {
      setError(err.message || 'Send failed.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="email-test-panel"
      className="rounded-lg border border-red-900/40 bg-gradient-to-br from-red-950/25 via-black to-zinc-950 p-4 sm:p-5 mb-5"
    >
      <div className="flex items-center gap-2 mb-1">
        <Mail className="h-4 w-4 text-red-400" />
        <p className="text-[10px] font-black tracking-[0.3em] uppercase text-red-400">
          Test story email
        </p>
      </div>
      <p className="text-xs text-gray-400 mb-3 leading-relaxed">
        Sends a one-off preview of the exact email subscribers get on publish, so you can
        eyeball formatting in your own inbox first. Subject is prefixed with
        <code className="mx-1 text-red-300">[TEST]</code>.
      </p>

      <form onSubmit={send} className="grid sm:grid-cols-[1fr_auto_auto] gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          data-testid="email-test-input"
          className="bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-sm text-white outline-none"
        />
        <select
          value={articleId}
          onChange={(e) => setArticleId(e.target.value)}
          data-testid="email-test-article"
          className="bg-zinc-950 border border-zinc-800 focus:border-red-500/60 rounded px-3 py-2 text-sm text-white outline-none max-w-[220px] truncate"
        >
          <option value="">Latest published</option>
          {articles.map((a) => (
            <option key={a.id} value={a.id}>{a.title}</option>
          ))}
        </select>
        <button
          type="submit"
          disabled={sending || !email.trim()}
          data-testid="email-test-send"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-gray-500 disabled:cursor-not-allowed text-white text-[11px] font-black uppercase tracking-widest shadow-[0_0_18px_rgba(220,38,38,0.35)]"
        >
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          {sending ? 'Sending…' : 'Send Test'}
        </button>
      </form>

      {result && (
        <div
          data-testid="email-test-result"
          className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded bg-emerald-950/40 border border-emerald-500/40 text-sm"
        >
          <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0 text-xs">
            <p className="font-black text-emerald-300 uppercase tracking-widest text-[11px]">
              Test email sent
            </p>
            <p className="text-emerald-200/80 mt-0.5 break-words">
              Delivered to <strong className="text-white">{result.to}</strong> · Article: <em className="text-white">{result.article?.title}</em>
            </p>
            <p className="text-gray-400 mt-0.5">
              From: <code className="text-gray-300">{result.from}</code>
            </p>
          </div>
        </div>
      )}
      {error && (
        <div
          data-testid="email-test-error"
          className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded bg-red-950/40 border border-red-500/50 text-sm"
        >
          <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
          <p className="text-red-300 text-xs break-words">{error}</p>
        </div>
      )}
    </div>
  );
};

export default TestEmailPanel;
